import axios from 'axios';

const PREFIX = 'tikz_svg_v1_';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 50;
const SERVER_BATCH_MAX = 50;
const SERVER_SVG_BYTE_CAP = 512_000;

const CACHE_FLAG = String(process.env.REACT_APP_ENABLE_TIKZ_SVG_CACHE ?? 'true').toLowerCase();
export const isTikzSvgCacheEnabled = CACHE_FLAG !== 'false' && CACHE_FLAG !== '0' && CACHE_FLAG !== 'off';

const hashCode = (value = '') => {
  // NOTE: 32-bit djb2-style hash; collisions are rare but possible.
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash) + value.charCodeAt(index);
    hash >>>= 0;
  }
  return hash.toString(16);
};

export const buildCacheKey = (tikzCode = '') => `${PREFIX}${hashCode(String(tikzCode))}_${String(tikzCode).length}`;

export const getCachedSvg = (tikzCode = '') => {
  if (!isTikzSvgCacheEnabled || typeof window === 'undefined' || !window.localStorage) return null;
  const key = buildCacheKey(tikzCode);

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.svg || !parsed?.cachedAt) return null;

    if ((Date.now() - parsed.cachedAt) > TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed.svg;
  } catch (_error) {
    return null;
  }
};

// Listeners notified whenever cacheSvg() successfully writes a new entry.
// Consumers (DiagramTile, DiagramViewerButton, OneQuestionAtATime's
// DiagramRenderer, the worksheet HTML re-renderer) subscribe to react when
// the background prerender lands an SVG so the UI swaps the "View Diagram"
// placeholder for the inline render without requiring a click.
const cacheUpdateListeners = new Set();

const notifyCacheUpdate = (tikzCode) => {
  if (cacheUpdateListeners.size === 0) return;
  for (const listener of Array.from(cacheUpdateListeners)) {
    try {
      listener(tikzCode);
    } catch (_listenerError) {
      // listener errors must not break the cache write
    }
  }
};

export const subscribeCacheUpdate = (listener) => {
  if (typeof listener !== 'function') return () => {};
  cacheUpdateListeners.add(listener);
  return () => cacheUpdateListeners.delete(listener);
};

export const cacheSvg = (tikzCode = '', svgHtml = '') => {
  if (!isTikzSvgCacheEnabled || typeof window === 'undefined' || !window.localStorage) return;
  if (!tikzCode || !svgHtml) return;
  const key = buildCacheKey(tikzCode);
  const value = JSON.stringify({ svg: svgHtml, cachedAt: Date.now() });
  let stored = false;
  try {
    window.localStorage.setItem(key, value);
    stored = true;
  } catch (_error) {
    try {
      pruneExpiredEntries();
      window.localStorage.setItem(key, value);
      stored = true;
    } catch (_retryError) {
      // Give up — quota still exhausted after prune.
    }
  }
  if (stored) notifyCacheUpdate(tikzCode);
};

export const pruneExpiredEntries = () => {
  if (!isTikzSvgCacheEnabled || typeof window === 'undefined' || !window.localStorage) return;
  const now = Date.now();
  const validEntries = [];
  Object.keys(window.localStorage)
    .filter((key) => key.startsWith(PREFIX))
    .forEach((key) => {
      try {
        const parsed = JSON.parse(window.localStorage.getItem(key));
        if (!parsed?.cachedAt || (now - parsed.cachedAt) > TTL_MS) {
          window.localStorage.removeItem(key);
          return;
        }
        validEntries.push({ key, cachedAt: parsed.cachedAt });
      } catch (_error) {
        window.localStorage.removeItem(key);
      }
    });

  if (validEntries.length > MAX_ENTRIES) {
    validEntries
      .sort((a, b) => a.cachedAt - b.cachedAt)
      .slice(0, validEntries.length - MAX_ENTRIES)
      .forEach(({ key }) => window.localStorage.removeItem(key));
  }
};

// Persist a single browser-rendered SVG onto a QB row so worksheets that
// later pull this question reuse the cached SVG. Server hash-matches the
// payload against the question's current diagram_code; stale tabs cannot
// overwrite freshly edited diagrams. Fire-and-forget.
export const postSvgCacheToQbQuestion = async (questionId, entry) => {
  if (!isTikzSvgCacheEnabled || !questionId || !entry || !entry.tikzCode || !entry.svgHtml) {
    return { ok: false, skipped: true };
  }
  if (entry.svgHtml.length > SERVER_SVG_BYTE_CAP) {
    return { ok: false, skipped: true };
  }
  try {
    await axios.post(`/qb/questions/${questionId}/svg-cache`, {
      tikz_hash: buildCacheKey(entry.tikzCode),
      svg_content: entry.svgHtml,
    });
    return { ok: true };
  } catch (_error) {
    return { ok: false, error: _error?.message || 'request failed' };
  }
};

// Persist a batch of browser-rendered SVGs to the worksheet record so every
// viewer (and the print/PDF export) gets the cached SVG inline without
// re-running TikZJax. The server matches each entry by hash against the
// diagram's sanitized code; mismatched/unknown hashes are silently dropped.
// Fire-and-forget: failures (auth, network, 404 worksheet) do not bubble up.
export const postSvgCacheToServer = async (worksheetId, entries = []) => {
  if (!isTikzSvgCacheEnabled || !worksheetId || !Array.isArray(entries) || entries.length === 0) {
    return { ok: false, skipped: true };
  }
  const payload = [];
  for (const entry of entries) {
    const tikzCode = entry && entry.tikzCode;
    const svgHtml = entry && entry.svgHtml;
    if (!tikzCode || !svgHtml) continue;
    if (svgHtml.length > SERVER_SVG_BYTE_CAP) continue;
    payload.push({ tikz_hash: buildCacheKey(tikzCode), svg_content: svgHtml });
    if (payload.length >= SERVER_BATCH_MAX) break;
  }
  if (payload.length === 0) return { ok: false, skipped: true };
  try {
    await axios.post(`/worksheets/${worksheetId}/tikz-svg-cache`, { entries: payload });
    return { ok: true, count: payload.length };
  } catch (_error) {
    return { ok: false, error: _error?.message || 'request failed' };
  }
};
